import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import * as XLSX from 'xlsx';

interface PartidoData {
  id: string;
  numero: number;
  denominacion: string;
  sigla: string;
  tipo: string;
  estado: string;
  afiliadosValidos: number;
  validos: number;
  noEncontrados: number;
  extranjeros: number;
  excluidosLegales: number;
  duplicadosValidos?: number;
  duplicadosInvalidos?: number;
  duplicados?: number;
  duplicadosDistrito?: number;
  localidadNoCoincide: number;
  aprobadosManuales: number;
  totalCargados: number;
  padronReferencia: number;
  minimoRequerido: number;
  cumple4PorMil: boolean;
}

function esMarginal(p: PartidoData) {
  return p.cumple4PorMil && p.afiliadosValidos < p.minimoRequerido * 1.1;
}

function getStatus(p: PartidoData): 'ok' | 'warn' | 'bad' {
  if (!p.cumple4PorMil) return 'bad';
  if (esMarginal(p)) return 'warn';
  return 'ok';
}

interface Props {
  modulo: 'afiliados' | 'control';
}

export default function DashboardResumenAfiliados({ modulo }: Props) {
  const navigate = useNavigate();
  const [barMode, setBarMode] = useState<'todos' | 'riesgo' | 'incumple'>('todos');
  const [tipoFilter, setTipoFilter] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const endpoint = modulo === 'afiliados' ? '/partidos/cumplimiento' : '/afiliados-control/cumplimiento';

  const { data: partidos = [], isLoading, isError } = useQuery<PartidoData[]>({
    queryKey: [`dashboard-cumplimiento-${modulo}`],
    queryFn: async () => {
      const r = await api.get(endpoint);
      return r.data.data ?? r.data;
    },
  });

  const stats = useMemo(() => {
    const cumplen = partidos.filter(p => p.cumple4PorMil && !esMarginal(p)).length;
    const enRiesgo = partidos.filter(esMarginal).length;
    const noCumplen = partidos.filter(p => !p.cumple4PorMil).length;
    const totalValidos = partidos.reduce((s, p) => s + p.afiliadosValidos, 0);
    const totalNoCont = partidos.reduce((s, p) => s + (p.totalCargados - p.afiliadosValidos), 0);
    const padronRef = partidos.length > 0 ? Math.max(...partidos.map(p => p.padronReferencia)) : 0;
    const minProv = partidos.length > 0 ? Math.max(...partidos.map(p => p.minimoRequerido)) : 0;
    return { cumplen, enRiesgo, noCumplen, totalValidos, totalNoCont, padronRef, minProv };
  }, [partidos]);

  const barPartidos = useMemo(() => {
    let f = [...partidos];
    if (barMode === 'riesgo') f = f.filter(p => !p.cumple4PorMil || esMarginal(p));
    if (barMode === 'incumple') f = f.filter(p => !p.cumple4PorMil);
    return f.sort((a, b) => b.afiliadosValidos - a.afiliadosValidos);
  }, [partidos, barMode]);

  const tablePartidos = useMemo(() => {
    let f = [...partidos];
    if (tipoFilter) f = f.filter(p => p.tipo === tipoFilter);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      f = f.filter(p => p.denominacion.toLowerCase().includes(q) || p.sigla?.toLowerCase().includes(q));
    }
    if (estadoFilter === 'validos') f = f.filter(p => p.validos > 0);
    if (estadoFilter === 'noEncontrados') f = f.filter(p => p.noEncontrados > 0);
    if (estadoFilter === 'extranjeros') f = f.filter(p => p.extranjeros > 0);
    if (estadoFilter === 'excluidosLegales') f = f.filter(p => p.excluidosLegales > 0);
    if (estadoFilter === 'duplicados') f = f.filter(p => ((p.duplicadosValidos ?? 0) + (p.duplicadosInvalidos ?? 0) + (p.duplicados ?? 0)) > 0);
    if (estadoFilter === 'duplicadosDistrito') f = f.filter(p => (p.duplicadosDistrito ?? 0) > 0);
    if (estadoFilter === 'localidadNoCoincide') f = f.filter(p => p.localidadNoCoincide > 0);
    if (estadoFilter === 'aprobadosManuales') f = f.filter(p => p.aprobadosManuales > 0);
    return f.sort((a, b) => ({ bad: 0, warn: 1, ok: 2 }[getStatus(a)] - { bad: 0, warn: 1, ok: 2 }[getStatus(b)]));
  }, [partidos, tipoFilter, estadoFilter, searchTerm]);

  const handlePartidoClick = (p: PartidoData) => {
    navigate(modulo === 'afiliados' ? `/afiliados?partido=${p.id}` : `/afiliados-control?partido=${p.id}`);
  };

  const exportarExcel = () => {
    const rows = tablePartidos.map(p => ({
      'Número': p.numero,
      'Denominación': p.denominacion,
      'Sigla': p.sigla,
      'Tipo': p.tipo,
      'Total cargados': p.totalCargados,
      'Válidos': p.validos,
      'No encontrado': p.noEncontrados,
      'Extranjero': p.extranjeros,
      'Excluido legal': p.excluidosLegales,
      ...(modulo === 'afiliados'
        ? { 'Dup. válido': p.duplicadosValidos ?? 0, 'Dup. inválido': p.duplicadosInvalidos ?? 0 }
        : { 'Duplicado': p.duplicados ?? 0 }),
      'Dup. distrito': p.duplicadosDistrito ?? 0,
      'Loc. no coincide': p.localidadNoCoincide,
      'Apr. manual': p.aprobadosManuales,
      'Afiliados válidos': p.afiliadosValidos,
      'Padrón referencia': p.padronReferencia,
      'Mínimo requerido': p.minimoRequerido,
      'Cumple 4‰': p.cumple4PorMil ? 'Sí' : 'No',
      'Diferencia': p.afiliadosValidos - p.minimoRequerido,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Resumen afiliados');
    XLSX.writeFile(wb, `resumen-afiliados-${modulo}-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const maxValidos = Math.max(...barPartidos.map(p => p.afiliadosValidos), 1);
  const circum = 2 * Math.PI * 50;
  const total = partidos.length || 1;
  const badDash = (stats.noCumplen / total) * circum;
  const warnDash = (stats.enRiesgo / total) * circum;
  const okDash = (stats.cumplen / total) * circum;

  if (isLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (isError) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-700 text-sm">
      Error al cargar el resumen. Intentá de nuevo.
    </div>
  );

  return (
    <div className="space-y-6">

      {/* CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Partidos activos</p>
          <p className="text-3xl font-extrabold text-gray-900">{partidos.length}</p>
          <p className="text-xs text-gray-500 mt-1">en el registro electoral</p>
          <span className="inline-block mt-2 text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">
            {partidos.filter(p => p.tipo === 'PROVINCIAL').length} prov · {partidos.filter(p => p.tipo === 'MUNICIPAL').length} mun · {partidos.filter(p => p.tipo === 'DISTRITO').length} dist
          </span>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Total afiliados válidos</p>
          <p className="text-3xl font-extrabold text-gray-900">{stats.totalValidos.toLocaleString('es-AR')}</p>
          <p className="text-xs text-gray-500 mt-1">válidos + aprobados manualmente{modulo === 'afiliados' ? ' + dup. válidos' : ''}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">No contabilizados</p>
          <p className="text-3xl font-extrabold text-gray-900">{stats.totalNoCont.toLocaleString('es-AR')}</p>
          <p className="text-xs text-gray-500 mt-1">extranjeros, inhabilitados, duplicados inválidos</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Padrón de referencia</p>
          <p className="text-3xl font-extrabold text-gray-900">{stats.padronRef.toLocaleString('es-AR')}</p>
          <p className="text-xs text-gray-500 mt-1">electores habilitados Chubut</p>
          <span className="inline-block mt-2 text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">
            mín. provincial: {stats.minProv.toLocaleString('es-AR')}
          </span>
        </div>
      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">

        {/* DONUT */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm font-semibold text-gray-500 mb-4">Cumplimiento 4‰</p>
          <div className="flex flex-col items-center gap-3">
            <svg width="100" height="100" viewBox="0 0 130 130" className="flex-shrink-0">
              <circle cx="65" cy="65" r="50" fill="none" stroke="#f1f5f9" strokeWidth="19" />
              <circle cx="65" cy="65" r="50" fill="none" stroke="#ef4444" strokeWidth="19"
                strokeDasharray={`${badDash} ${circum}`} strokeDashoffset="0" transform="rotate(-90 65 65)" />
              <circle cx="65" cy="65" r="50" fill="none" stroke="#f59e0b" strokeWidth="19"
                strokeDasharray={`${warnDash} ${circum}`} strokeDashoffset={`${-badDash}`} transform="rotate(-90 65 65)" />
              <circle cx="65" cy="65" r="50" fill="none" stroke="#22c55e" strokeWidth="19"
                strokeDasharray={`${okDash} ${circum}`} strokeDashoffset={`${-(badDash + warnDash)}`} transform="rotate(-90 65 65)" />
              <text x="65" y="60" textAnchor="middle" fontSize="20" fontWeight="800" fill="#1e293b">{stats.cumplen}/{partidos.length}</text>
              <text x="65" y="75" textAnchor="middle" fontSize="10" fill="#64748b">cumplen</text>
            </svg>
            <div className="flex flex-col gap-1.5 w-full">
              {([['bg-green-500', 'Cumplen', stats.cumplen], ['bg-amber-400', 'En riesgo', stats.enRiesgo], ['bg-red-500', 'No cumplen', stats.noCumplen]] as const).map(([color, label, val]) => (
                <div key={label} className="flex items-center gap-2 text-sm">
                  <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${color}`} />
                  <span className="text-gray-500 flex-1">{label}</span>
                  <span className="font-bold text-gray-900">{val}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* BAR CHART */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm font-semibold text-gray-500">Afiliados válidos vs. mínimo</p>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${barMode === 'todos' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                {barMode === 'todos' ? `${barPartidos.length} partidos` : barMode === 'riesgo' ? `${barPartidos.length} en riesgo o incumplimiento` : `${barPartidos.length} no cumplen`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Mostrar:</span>
              <div className="flex bg-gray-100 rounded-full p-0.5 border border-gray-200 gap-0.5">
                {(['todos', 'riesgo', 'incumple'] as const).map(m => (
                  <button key={m} onClick={() => setBarMode(m)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      barMode === m
                        ? m === 'todos' ? 'bg-blue-700 text-white' : m === 'riesgo' ? 'bg-amber-600 text-white' : 'bg-red-700 text-white'
                        : 'text-gray-500 hover:text-gray-800'
                    }`}>
                    {m === 'todos' ? 'Todos' : m === 'riesgo' ? '⚠ En riesgo' : '✕ No cumplen'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {barPartidos.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p className="text-2xl mb-2">✅</p>
              <p className="text-sm">Todos los partidos cumplen el 4‰</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-72 flex flex-col gap-1.5 pr-1">
              {barPartidos.map(p => {
                const status = getStatus(p);
                const pct = Math.min((p.afiliadosValidos / maxValidos) * 100, 100);
                const minPct = Math.min((p.minimoRequerido / maxValidos) * 100, 100);
                const barGrad = status === 'ok' ? 'from-green-600 to-green-400' : status === 'warn' ? 'from-amber-600 to-amber-400' : 'from-red-700 to-red-500';
                const labelCls = status === 'bad' ? 'text-red-700 font-semibold' : status === 'warn' ? 'text-amber-700 font-semibold' : 'text-gray-500';
                return (
                  <div key={p.id} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0 overflow-hidden text-ellipsis whitespace-nowrap font-medium" title={p.denominacion}>
                      {p.sigla || p.denominacion}
                    </span>
                    <div className="flex-1 h-5 bg-gray-100 rounded relative overflow-visible">
                      <div className={`h-full rounded bg-gradient-to-r ${barGrad} flex items-center pl-1.5`} style={{ width: `${pct}%`, minWidth: '2px' }}>
                        {pct > 15 && <span className="text-white text-[10px] font-bold">{p.afiliadosValidos.toLocaleString('es-AR')}</span>}
                      </div>
                      <div className="absolute top-[-3px] bottom-[-3px] w-0.5 bg-gray-800 opacity-20 rounded" style={{ left: `${minPct}%` }} />
                    </div>
                    <span className={`text-[10px] w-20 text-right flex-shrink-0 ${labelCls}`}>
                      mín. {p.minimoRequerido.toLocaleString('es-AR')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-3 text-[10px] text-gray-400 flex items-center gap-1 flex-wrap">
            <span className="inline-block w-0.5 h-3 bg-gray-800 opacity-25 rounded" />
            Línea vertical = mínimo requerido.
            <span className="text-green-700 font-semibold">■ Verde</span> cumple holgadamente.
            <span className="text-amber-700 font-semibold ml-1">■ Amarillo</span> margen &lt;10%.
            <span className="text-red-700 font-semibold ml-1">■ Rojo</span> no cumple.
          </p>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm font-bold text-gray-900">📋 Detalle por partido — todos los estados de validación</p>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">Tipo de partido: Todos</option>
              <option value="PROVINCIAL">Provincial</option>
              <option value="MUNICIPAL">Municipal</option>
              <option value="DISTRITO">Distrito</option>
            </select>
            <select value={estadoFilter} onChange={e => setEstadoFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
              <option value="">Estado de afiliados: Todos</option>
              <option value="validos">Válidos</option>
              <option value="noEncontrados">No encontrado</option>
              <option value="extranjeros">Extranjero</option>
              <option value="excluidosLegales">Excluido legal</option>
              <option value="duplicados">Duplicado</option>
              <option value="duplicadosDistrito">Duplicado (distrito)</option>
              <option value="localidadNoCoincide">Loc. no coincide</option>
              <option value="aprobadosManuales">Aprobado manual</option>
            </select>
            <input type="search" placeholder="Buscar partido…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-44 focus:ring-2 focus:ring-blue-500 focus:outline-none" />
            <button onClick={exportarExcel}
              className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1.5">
              ⬇ Excel
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider min-w-[200px]">Partido</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Total</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-green-700 uppercase tracking-wider">Válidos</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">No enc.</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Extran.</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Excl. legal</th>
                {modulo === 'afiliados' ? (
                  <>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Dup. válido</th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Dup. inválido</th>
                  </>
                ) : (
                  <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Duplicado</th>
                )}
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-rose-600 uppercase tracking-wider">Dup. distrito</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Loc. NC</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-500 uppercase tracking-wider">Apr. manual</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold text-blue-700 uppercase tracking-wider min-w-[210px]">4‰ Cumplimiento</th>
              </tr>
            </thead>
            <tbody>
              {tablePartidos.map(p => {
                const status = getStatus(p);
                const rowBg = status === 'bad' ? 'bg-red-50' : status === 'warn' ? 'bg-amber-50' : '';
                const diff = p.afiliadosValidos - p.minimoRequerido;
                const tipoBadge = p.tipo === 'PROVINCIAL' ? 'bg-violet-100 text-violet-700' : p.tipo === 'MUNICIPAL' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700';
                const tipoLabel = p.tipo.charAt(0) + p.tipo.slice(1).toLowerCase();
                const validosCls = status === 'bad' ? 'text-red-700 font-bold' : status === 'warn' ? 'text-amber-700 font-bold' : 'text-green-700 font-bold';
                return (
                  <tr key={p.id} className={`border-b border-gray-50 hover:brightness-95 transition-all ${rowBg}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => handlePartidoClick(p)} className="text-left font-bold text-gray-900 text-sm hover:text-blue-600 hover:underline">
                          {p.denominacion} <span className="font-normal text-gray-400 text-xs">({p.sigla || `N° ${p.numero}`})</span>
                        </button>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full w-fit ${tipoBadge}`}>{tipoLabel}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${tipoBadge}`}>{tipoLabel}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-gray-700 tabular-nums">{p.totalCargados.toLocaleString('es-AR')}</td>
                    <td className={`px-3 py-2.5 text-right tabular-nums ${validosCls}`}>{p.validos.toLocaleString('es-AR')}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-600">{p.noEncontrados > 0 ? p.noEncontrados.toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-orange-600">{p.extranjeros > 0 ? p.extranjeros.toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{p.excluidosLegales > 0 ? p.excluidosLegales.toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    {modulo === 'afiliados' ? (
                      <>
                        <td className="px-3 py-2.5 text-right tabular-nums text-violet-600">{(p.duplicadosValidos ?? 0) > 0 ? (p.duplicadosValidos ?? 0).toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-violet-800">{(p.duplicadosInvalidos ?? 0) > 0 ? (p.duplicadosInvalidos ?? 0).toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                      </>
                    ) : (
                      <td className="px-3 py-2.5 text-right tabular-nums text-violet-600">{(p.duplicados ?? 0) > 0 ? (p.duplicados ?? 0).toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    )}
                    <td className="px-3 py-2.5 text-right tabular-nums text-rose-600 font-semibold">{(p.duplicadosDistrito ?? 0) > 0 ? (p.duplicadosDistrito ?? 0).toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-indigo-600">{p.localidadNoCoincide > 0 ? p.localidadNoCoincide.toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-cyan-700 font-semibold">{p.aprobadosManuales > 0 ? p.aprobadosManuales.toLocaleString('es-AR') : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-start gap-1.5">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5 ${status === 'ok' ? 'bg-green-500' : status === 'warn' ? 'bg-amber-400' : 'bg-red-500'}`} />
                          <span className={`text-xs font-extrabold ${status === 'ok' ? 'text-green-700' : status === 'warn' ? 'text-amber-700' : 'text-red-700'}`}>
                            {status === 'ok' ? 'CUMPLE' : status === 'warn' ? 'MARGEN AJUSTADO' : 'NO CUMPLE'}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-500 pl-4 leading-snug">
                          {p.afiliadosValidos.toLocaleString('es-AR')} válidos / mín. {p.minimoRequerido.toLocaleString('es-AR')}<br />
                          {diff >= 0
                            ? <span className={status === 'warn' ? 'text-amber-700 font-semibold' : 'text-green-700 font-semibold'}>
                                +{diff.toLocaleString('es-AR')} sobre el mínimo (+{Math.round(((p.afiliadosValidos - p.minimoRequerido) / p.minimoRequerido) * 100)}% de margen){status === 'warn' ? ' — Riesgo' : ''}
                              </span>
                            : <span className="text-red-700 font-semibold">Faltan {Math.abs(diff).toLocaleString('es-AR')} afiliados válidos</span>
                          }
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {tablePartidos.length === 0 && (
                <tr><td colSpan={modulo === 'afiliados' ? 13 : 12} className="text-center py-8 text-gray-400 text-sm">No hay partidos con ese filtro</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* NOTAS */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 leading-relaxed">
        <strong className="font-bold block mb-1">⚠ Notas y leyenda</strong>
        <span className="font-semibold">No contabilizados:</span> afiliados rechazados que no suman al total de válidos: con datos inválidos, extranjeros registrados en el padrón de extranjeros, e inhabilitados judiciales.<br />
        <span className="font-semibold">Margen ajustado:</span> partidos que cumplen con menos del 110% sobre el mínimo requerido — en riesgo ante cualquier baja.<br />
        <span className="font-semibold">Mínimo requerido:</span> máx(25, techo(padrón × 0,004)) según Ley XII N° 21. Padrón provincial {stats.padronRef.toLocaleString('es-AR')} → mínimo {stats.minProv.toLocaleString('es-AR')} afiliados válidos.
      </div>

    </div>
  );
}
